import React, { createContext, useContext, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { lensPath, lensProp, set, view, split, merge } from "ramda"
import { handleValidationError, validateWithSchema } from "../../services/yup"

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const DEFAULT_SEPARATOR = "."

// --------------- 𝕌𝕥𝕚𝕝𝕤 ---------------

export const FormContext = createContext({ values: {} })
FormContext.displayName = "FormContext"

export const useCurrentForm = () => useContext(FormContext)

export const useForm = ({ readOnly = false, initialValues = {} } = {}) => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({})

  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (event, separator) => {
    if (!readOnly) {
      const {
        target: { name, value },
      } = event

      setFieldValue(name, value, separator)
    }
  }

  const getData = () => ({ ...values })

  const getFieldValue = (fieldPath, separator = DEFAULT_SEPARATOR) =>
    view(lensPath(split(separator, fieldPath)), values)

  const setData = (newData) => setValues((previous) => merge(previous, newData))

  const setFieldValue = (fieldPath, newValue, splitter = DEFAULT_SEPARATOR) => {
    const fieldLens = lensPath(split(splitter, fieldPath))
    setValues(set(fieldLens, newValue))
  }

  const setFieldError = (errorPath, value) => {
    const fieldLens = lensProp(errorPath)
    setErrors(set(fieldLens, value))
  }

  const reset = useCallback(() => {
    setValues(initialValues)
    setErrors({})
  }, [initialValues])

  return {
    values,
    errors,
    readOnly,
    getData,
    setData,
    setValues,
    setErrors,
    setFieldError,
    getFieldValue,
    setFieldValue,
    handleChange,
    reset,
    //,
    isSubmitted,
    setIsSubmitted,
  }
}

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export function FormProvider({ form, children }) {
  return <FormContext.Provider value={form}>{children}</FormContext.Provider>
}

export function Form({
  formRef,
  children,
  onSubmit,
  initialData,
  schema,
  ...props
}) {
  const form = useForm({ initialValues: initialData })

  const handleSubmit = (event, values, { reset }) => {
    event.preventDefault()
    form.setIsSubmitted(true)

    if (schema) {
      form.setErrors({})
      validateWithSchema(
        schema,
        values,
        (values) => onSubmit(values, { reset }, event),
        handleValidationError(form)
      )
    } else onSubmit(values, { reset }, event)
  }

  return (
    <FormProvider form={form}>
      <FormContext.Consumer>
        {({ values, reset } = {}) => (
          <form
            ref={(node) =>
              formRef
                ? (formRef.current = {
                    ...form,
                    FormComponent: node,
                  })
                : null
            }
            onSubmit={(e) => handleSubmit(e, values, { reset })}
            {...props}
          >
            {children}
          </form>
        )}
      </FormContext.Consumer>
    </FormProvider>
  )
}

Form.propTypes = {
  schema: PropTypes.object,
  initialData: PropTypes.object,
  formRef: PropTypes.exact({
    current: PropTypes.any,
  }),
  onSubmit: PropTypes.func,
}

export * from "./components"
